<template>
    <!-- This template is used on frontend to get branded assets -->
    <div class="bg-[#F5F5F5] rounded-lg p-6">
        <!-- Customer Branding Settings -->
        <div v-if="canEdit" class="p-6 mb-8 bg-white rounded-lg border border-gray-200 shadow-sm">
            <h3 class="pb-2 mb-6 text-lg font-semibold text-gray-900 border-b">Brand Settings</h3>

            <div class="grid grid-cols-1 gap-8 mb-6 lg:grid-cols-2">
                <!-- Logo -->
                <div class="space-y-3">
                    <label class="block text-sm font-semibold text-gray-900">Logo</label>
                    <div class="flex flex-col gap-4">
                        <div
                            class="flex overflow-hidden relative justify-center items-center w-full bg-gray-50 rounded-lg border aspect-video"
                        >
                            <img v-if="getLogoUrl" :src="getLogoUrl" class="max-w-full max-h-[300px] object-cover" />
                            <span v-else class="text-xs text-gray-400">No Logo</span>
                        </div>
                        <div class="w-full">
                            <input
                                ref="logoInput"
                                type="file"
                                class="block w-full text-sm text-gray-500 transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                accept="image/*"
                                @change="handleLogoChange"
                            />
                            <p class="mt-2 text-xs text-gray-500">Recommended: PNG or WebP. Max 1MB.</p>
                        </div>
                    </div>
                </div>

                <!-- Website URL -->
                <div class="space-y-3">
                    <label class="block text-sm font-semibold text-gray-900">Website URL</label>
                    <div
                        class="bg-gray-50 p-4 rounded-xl border border-gray-200 max-h-[200px] flex flex-col justify-center"
                    >
                        <p class="mb-2 text-xs text-gray-500">
                            Enter your company website URL to link branded assets back to your site.
                        </p>
                        <input
                            v-model="localWebsiteUrl"
                            type="url"
                            class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            placeholder="https://example.com"
                        />
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-1 gap-8 mb-6 lg:grid-cols-3">
                <!-- Brand Colour -->
                <div class="space-y-3">
                    <label
                        for="brand_colour"
                        class="flex items-center justify-between text-sm font-semibold text-gray-900"
                    >
                        Brand Colour
                        <button
                            type="button"
                            class="text-sm text-red-600 bg-white hover:text-red-900"
                            @click="clearInput('brand_colour')"
                        >
                            <i class="text-red-600 fas fa-eraser hover:text-red-900"></i>
                        </button>
                    </label>
                    <div class="p-4 bg-gray-50 rounded-xl border border-gray-200">
                        <div class="flex gap-4 items-center">
                            <input
                                :value="formatColorForPicker(localBrandColour, '#073141')"
                                type="color"
                                class="w-20 h-10 rounded border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                @input="localBrandColour = $event.target.value"
                            />
                            <input
                                v-model="localBrandColour"
                                type="text"
                                class="flex-1 font-mono text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="#073141"
                            />
                        </div>
                    </div>
                    <p v-if="errors.brand_colour" class="mt-2 text-xs text-red-500">
                        {{ errors.brand_colour[0] }}
                    </p>
                </div>

                <!-- Text Colour -->
                <div class="space-y-3">
                    <label
                        for="text_colour"
                        class="flex items-center justify-between text-sm font-semibold text-gray-900"
                    >
                        Text Colour
                        <button
                            type="button"
                            class="text-sm text-red-600 bg-white hover:text-red-900"
                            @click="clearInput('text_colour')"
                        >
                            <i class="text-red-600 fas fa-eraser hover:text-red-900"></i>
                        </button>
                    </label>
                    <div class="p-4 bg-gray-50 rounded-xl border border-gray-200">
                        <div class="flex gap-4 items-center">
                            <input
                                :value="formatColorForPicker(localTextColour, '#083141')"
                                type="color"
                                class="w-20 h-10 rounded border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                @input="localTextColour = $event.target.value"
                            />
                            <input
                                v-model="localTextColour"
                                type="text"
                                class="flex-1 font-mono text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="#000000"
                            />
                        </div>
                    </div>
                    <p v-if="errors.text_colour" class="mt-2 text-xs text-red-500">
                        {{ errors.text_colour[0] }}
                    </p>
                </div>

                <!-- Background Colour -->
                <div class="space-y-3">
                    <label
                        for="background_colour"
                        class="flex items-center justify-between text-sm font-semibold text-gray-900"
                    >
                        Background Colour
                        <button
                            type="button"
                            class="text-sm text-red-600 bg-white hover:text-red-900"
                            @click="clearInput('background_colour')"
                        >
                            <i class="text-red-600 fas fa-eraser hover:text-red-900"></i>
                        </button>
                    </label>
                    <div class="p-4 bg-gray-50 rounded-xl border border-gray-200">
                        <div class="flex gap-4 items-center">
                            <input
                                :value="formatColorForPicker(localBackgroundColour, '#f5f5f5')"
                                type="color"
                                class="w-20 h-10 rounded border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                @input="localBackgroundColour = $event.target.value"
                            />
                            <input
                                v-model="localBackgroundColour"
                                type="text"
                                class="flex-1 font-mono text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="#f5f5f5"
                            />
                        </div>
                    </div>
                    <p v-if="errors.background_colour" class="mt-2 text-xs text-red-500">
                        {{ errors.background_colour[0] }}
                    </p>
                </div>
            </div>

            <!-- Save Button -->
            <div class="flex justify-end pt-4 mt-4 border-t border-gray-100">
                <button
                    type="button"
                    class="inline-flex justify-center items-center px-4 py-2 text-sm font-semibold text-white rounded-md shadow-sm transition-colors duration-200 bg-midnight-600 hover:bg-midnight-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-midnight-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="isSaving"
                    @click="saveSettings"
                >
                    <i v-if="isSaving" class="mr-2 fa-solid fa-spinner-third fa-spin"></i>
                    {{ isSaving ? 'Saving...' : 'Save Settings' }}
                </button>
            </div>
        </div>

        <div class="p-6 mb-8 bg-white rounded-lg border border-gray-200 shadow-sm">
            <h3 class="pb-2 mb-6 text-lg font-semibold text-gray-900 border-b">Branded Assets</h3>
            <div v-if="loading" class="flex justify-center items-center py-12">
                <i class="text-3xl text-gray-400 fa-solid fa-spinner-third fa-spin"></i>
            </div>
            <div v-else-if="brandedTemplates.length" class="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
                <div v-for="tpl in brandedTemplates" :key="tpl.key" class="p-4 bg-gray-50 rounded-lg border shadow-sm">
                    <h4 class="mb-2 text-sm font-medium text-gray-900">{{ tpl.name }}</h4>
                    <div class="relative">
                        <img
                            :key="getTemplateUrl(tpl)"
                            :src="getTemplateUrl(tpl)"
                            :alt="tpl.name"
                            class="mb-3 w-full bg-gray-50 rounded border"
                            loading="lazy"
                            @load="imageLoaded(tpl)"
                            @error="imageLoaded(tpl)"
                        />
                        <div
                            v-if="tpl.uploading != null || tpl.imageLoading"
                            class="flex absolute inset-0 justify-center items-center rounded bg-black/30"
                        >
                            <i class="text-2xl text-white fa-solid fa-spinner-third fa-spin"></i>
                        </div>
                    </div>

                    <div class="mt-3 space-y-2">
                        <!-- Dynamic Slots -->
                        <div v-if="tpl.slots && tpl.slots.length > 0">
                            <div
                                v-for="slot in tpl.slots"
                                :key="slot.index"
                                class="flex justify-between items-center p-2 text-sm bg-gray-50 rounded"
                            >
                                <span class="pr-2 font-medium text-gray-700 truncate">{{ slot.label }}</span>
                                <div class="flex flex-shrink-0 gap-1 items-center">
                                    <input
                                        :id="'file-' + tpl.key + '-' + slot.index"
                                        type="file"
                                        class="hidden"
                                        accept="image/*"
                                        @change="handleUpload($event, tpl, slot.index)"
                                    />
                                    <button
                                        type="button"
                                        class="px-2 py-1 text-xs bg-white rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
                                        :disabled="tpl.uploading === slot.index"
                                        @click="triggerUpload(tpl, slot.index)"
                                    >
                                        <i
                                            v-if="tpl.uploading === slot.index"
                                            class="mr-2 fa-solid fa-spinner-third fa-spin"
                                        ></i>
                                        <span>{{ tpl.uploading === slot.index ? 'Replacing...' : 'Replace' }}</span>
                                    </button>
                                    <button
                                        v-if="slot.has_custom_image"
                                        type="button"
                                        class="flex justify-center items-center px-2 py-1 text-sm text-white bg-red-500 rounded-full border border-red-400 shadow-sm transition-colors duration-200 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                        :disabled="tpl.uploading === slot.index"
                                        title="Remove custom image"
                                        @click="removeImage(tpl, slot.index)"
                                    >
                                        <span class="text-white">×</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Fallback if no slots detected -->
                        <div v-else class="flex justify-between items-center p-2 bg-gray-50 rounded">
                            <span class="text-sm font-medium text-gray-700">Main Image</span>
                            <div class="flex flex-shrink-0 gap-1 items-center">
                                <button
                                    v-if="tpl.has_custom_main_image"
                                    type="button"
                                    class="px-2 py-1 text-xs text-red-500 bg-white rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
                                    :disabled="tpl.uploading === 0"
                                    title="Remove custom image"
                                    @click="removeImage(tpl, 0)"
                                >
                                    <i class="fa-solid fa-trash"></i>
                                </button>
                                <input
                                    :id="'file-' + tpl.key + '-0'"
                                    type="file"
                                    class="hidden"
                                    accept="image/*"
                                    @change="handleUpload($event, tpl, 0)"
                                />
                                <button
                                    type="button"
                                    class="px-2 py-1 text-xs bg-white rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
                                    :disabled="tpl.uploading === 0"
                                    @click="triggerUpload(tpl, 0)"
                                >
                                    <i v-if="tpl.uploading === 0" class="mr-2 fa-solid fa-spinner-third fa-spin"></i>
                                    <span>{{ tpl.uploading === 0 ? 'Replacing...' : 'Replace' }}</span>
                                </button>
                            </div>
                        </div>

                        <!-- Fallback / Actions -->
                        <div class="flex flex-col gap-3 justify-end pt-2 md:flex-row">
                            <a
                                :href="getTemplateUrl(tpl)"
                                target="_blank"
                                class="inline-flex justify-center items-center px-3.5 py-2.5 w-full text-sm font-semibold text-gray-900 bg-white rounded-md ring-1 ring-inset ring-gray-300 shadow-sm transition-colors duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyber-500 md:w-auto"
                            >
                                <i class="mr-2 text-gray-500 fa-solid fa-eye"></i>
                                Preview
                            </a>
                            <button
                                class="inline-flex justify-center items-center px-3.5 py-2.5 w-full text-sm font-semibold text-white rounded-md shadow-sm transition-colors duration-200 bg-cyber-600 hover:bg-cyber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyber-500 disabled:opacity-50 disabled:cursor-not-allowed md:w-auto"
                                :disabled="tpl.downloading"
                                @click="downloadPNG(tpl)"
                            >
                                <i v-if="tpl.downloading" class="mr-2 fa-solid fa-spinner-third fa-spin"></i>
                                <i v-else class="mr-2 fa-solid fa-download"></i>
                                {{ tpl.downloading ? 'Preparing...' : 'Download PNG' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <p v-else class="text-sm italic text-gray-500">No templates found.</p>
        </div>
    </div>
</template>

<script>
export default {
    inject: ['showNotification'],
    props: {
        // No customerSlug needed, inferred from auth
        brandColors: {
            type: Array,
            default: () => [],
        },
        brandColour: {
            type: String,
            default: null,
        },
        backgroundColour: {
            type: String,
            default: null,
        },
        textColour: {
            type: String,
            default: null,
        },
        websiteUrl: {
            type: String,
            default: null,
        },
        customerLogo: {
            type: [String, File],
            default: null,
        },
        customer: {
            type: Object,
            default: () => ({}),
        },
        canEdit: {
            type: Boolean,
            default: false,
        },
        useDashboardRoutes: {
            type: Boolean,
            default: false,
        },
        updateUrl: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            loading: true,
            brandedTemplates: [],
            localBrandColour: this.brandColour,
            localBackgroundColour: this.backgroundColour,
            localTextColour: this.textColour,
            localWebsiteUrl: this.websiteUrl,
            localCustomerLogo: null,
            isSaving: false,
            errors: {},
            settingsVersion: Date.now(),
            debouncedBrandColour: this.brandColour,
            debouncedBackgroundColour: this.backgroundColour,
            debouncedTextColour: this.textColour,
            debouncedWebsiteUrl: this.websiteUrl,
        };
    },
    computed: {
        getLogoUrl() {
            const logo = this.localCustomerLogo || this.customerLogo;
            if (!logo) {
                return null;
            }
            if (logo instanceof File) {
                return URL.createObjectURL(logo);
            }
            return logo;
        },
    },
    watch: {
        localBrandColour(val) {
            this.updateBrand(val);
        },
        localBackgroundColour(val) {
            this.updateBackground(val);
        },
        localTextColour(val) {
            this.updateText(val);
        },
        localWebsiteUrl(val) {
            this.updateWebsite(val);
        },
        localCustomerLogo() {
            this.refreshTemplatesDebounced();
        },
    },
    created() {
        this.updateBrand = this.debounce((val) => (this.debouncedBrandColour = val), 500);
        this.updateBackground = this.debounce((val) => (this.debouncedBackgroundColour = val), 500);
        this.updateText = this.debounce((val) => (this.debouncedTextColour = val), 500);
        this.updateWebsite = this.debounce((val) => (this.debouncedWebsiteUrl = val), 500);
        this.refreshTemplatesDebounced = this.debounce(this.refreshTemplates, 500);
    },
    mounted() {
        this.fetchTemplates();
    },
    methods: {
        debounce(func, wait) {
            let timeout;
            return function (...args) {
                const context = this;
                const later = function () {
                    timeout = null;
                    func.apply(context, args);
                };
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            };
        },
        refreshTemplates() {
            if (this.brandedTemplates && this.brandedTemplates.length) {
                this.brandedTemplates.forEach((tpl) => {
                    tpl.cacheKey = Date.now();
                });
            }
        },
        validateImageFile(file) {
            const maxBytes = 1024 * 1024;
            const acceptedMimeTypes = ['image/jpeg', 'image/png', 'image/webp'];
            const acceptedNamePattern = /\.(jpe?g|png|webp)$/i;

            const nameOk = acceptedNamePattern.test(file?.name || '');
            const typeOk = !file?.type || acceptedMimeTypes.includes(String(file.type).toLowerCase());
            const sizeOk = typeof file?.size === 'number' && file.size <= maxBytes;

            if (!nameOk || !typeOk) {
                this.showNotification('error', 'Upload a JPG, PNG, or WebP image.');
                return false;
            }
            if (!sizeOk) {
                this.showNotification('error', 'Image must be 1MB or less.');
                return false;
            }
            return true;
        },
        handleLogoChange(event) {
            const file = event.target.files[0];
            if (file && this.validateImageFile(file)) {
                this.localCustomerLogo = file;
                return;
            }
            event.target.value = '';
        },
        formatColorForPicker(color, fallback = '#000000') {
            if (!color) return fallback;
            let c = String(color).trim();
            if (c.startsWith('#')) {
                c = c.substring(1);
            }
            // Expand short hex
            if (c.length === 3) {
                c = c
                    .split('')
                    .map((x) => x + x)
                    .join('');
            }
            // Check if valid hex
            if (/^[0-9A-Fa-f]{6}$/.test(c)) {
                return '#' + c;
            }
            return fallback;
        },
        saveSettings() {
            this.isSaving = true;
            const formData = new FormData();
            // formData.append('_method', 'PUT');

            // Append values even if empty to allow clearing them
            formData.append('brand_colour', this.localBrandColour || '');
            formData.append('background_colour', this.localBackgroundColour || '');
            formData.append('text_colour', this.localTextColour || '');
            formData.append('website_url', this.localWebsiteUrl || '');

            if (this.localCustomerLogo instanceof File) {
                formData.append('logo', this.localCustomerLogo);
            }

            // The backend might not support PUT with multipart/form-data correctly in all environments
            // often it's better to POST with _method=PUT, but here we used axios.post
            // However, we deleted _method below? Wait.

            let url = this.updateUrl;
            if (!url) {
                if (typeof route === 'function' && route().has('profile.branded-assets.update')) {
                    url = route('profile.branded-assets.update');
                } else {
                    url = '/user/profile/branded-assets';
                }
            }

            // Do NOT delete _method if you want to simulate PUT via POST
            // formData.delete('_method');

            axios
                .post(url, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .then((response) => {
                    if (response.data.logo_url) {
                        this.localCustomerLogo = response.data.logo_url;
                    }

                    // Increment settings version to force template URL regeneration
                    this.settingsVersion = Date.now();

                    // Refresh templates to reflect new brand settings
                    this.fetchTemplates();

                    this.showNotification('success', 'Settings saved successfully');
                    this.errors = {};
                })
                .catch((error) => {
                    console.error('Error saving settings:', error);
                    if (error.response?.data?.errors) {
                        this.errors = error.response.data.errors;
                    }
                    this.showNotification(
                        'error',
                        'Failed to save settings: ' + (error.response?.data?.message || error.message),
                    );
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },
        fetchTemplates() {
            this.loading = true;

            const routeName = this.useDashboardRoutes
                ? 'dashboard.branded-assets.context.for-customer'
                : 'branded-assets.context';

            const params = this.useDashboardRoutes ? { customer: this.customer.slug } : {};

            axios
                .get(route(routeName, params))
                .then((res) => {
                    this.brandedTemplates = (res.data && res.data.templates ? res.data.templates : []).map((t) => ({
                        ...t,
                        cacheKey: Date.now(),
                        uploading: null,
                        imageLoading: false,
                        downloading: false,
                    }));
                    if (res.data && res.data.prefill) {
                        this.localBrandColour = res.data.prefill.brand_colour;
                        this.localBackgroundColour = res.data.prefill.background_colour;
                        this.localTextColour = res.data.prefill.text_colour;
                        this.localWebsiteUrl = res.data.prefill.website_url;
                        this.localCustomerLogo = res.data.prefill.logo_url;

                        // Also update debounced values immediately to avoid flicker
                        this.debouncedBrandColour = res.data.prefill.brand_colour;
                        this.debouncedBackgroundColour = res.data.prefill.background_colour;
                        this.debouncedTextColour = res.data.prefill.text_colour;
                        this.debouncedWebsiteUrl = res.data.prefill.website_url;
                    }
                })
                .catch((error) => {
                    console.error('Error fetching templates:', error);
                    this.brandedTemplates = [];
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getTemplateUrl(tpl) {
            const currentBrandColour = this.debouncedBrandColour || this.brandColour;
            // Only try to look up in brandColors if it's NOT a hex code
            let brand = currentBrandColour;
            if (currentBrandColour && !currentBrandColour.startsWith('#')) {
                brand = this.brandColors.find((c) => c.value === currentBrandColour)?.hex || '#073141';
            } else if (!brand) {
                brand = '#073141';
            }
            const website = this.debouncedWebsiteUrl || this.websiteUrl || '';
            const logoUrl = this.getLogoUrl || '';

            const qsObj = {
                brand_colour: brand,
                website_url: website,
                t: tpl.cacheKey || Date.now(), // Cache busting
                v: this.settingsVersion, // Settings version for logo updates
            };

            if (this.debouncedTextColour) {
                qsObj.text_colour = this.debouncedTextColour;
            }

            if (this.debouncedBackgroundColour) {
                qsObj.background_colour = this.debouncedBackgroundColour;
            }

            if (logoUrl && !logoUrl.startsWith('blob:')) {
                qsObj.logo_url = logoUrl;
            }

            const qs = new URLSearchParams(qsObj).toString();
            const routeName = this.useDashboardRoutes
                ? 'dashboard.branded-assets.generate.for-customer'
                : 'branded-assets.generate';
            const params = this.useDashboardRoutes
                ? { customer: this.customer.slug, template: tpl.key }
                : { template: tpl.key };

            return route(routeName, params) + '?' + qs;
        },
        triggerUpload(tpl, slotIndex) {
            document.getElementById('file-' + tpl.key + '-' + slotIndex).click();
        },
        handleUpload(event, tpl, slotIndex) {
            const file = event.target.files[0];
            if (!file) return;
            if (!this.validateImageFile(file)) {
                event.target.value = '';
                return;
            }

            tpl.uploading = slotIndex;

            const formData = new FormData();
            formData.append('image', file);
            formData.append('slot', slotIndex);

            const routeName = this.useDashboardRoutes
                ? 'dashboard.branded-assets.upload-image.for-customer'
                : 'branded-assets.upload-image';
            const params = this.useDashboardRoutes
                ? { customer: this.customer.slug, template: tpl.key }
                : { template: tpl.key };
            const url = route(routeName, params);

            axios
                .post(url, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .then((res) => {
                    tpl.cacheKey = Date.now();
                    tpl.imageLoading = true; // Start loading spinner for image fetch

                    // Safety timeout
                    setTimeout(() => {
                        if (tpl.imageLoading) tpl.imageLoading = false;
                    }, 5000);

                    if (tpl.slots && tpl.slots.length > 0) {
                        const slot = tpl.slots.find((s) => s.index === slotIndex);
                        if (slot) slot.has_custom_image = true;
                    } else {
                        tpl.has_custom_main_image = true;
                    }
                })
                .catch((error) => {
                    console.error('Error uploading image:', error);
                    this.showNotification('error', error.response?.data?.message || 'Failed to upload image');
                })
                .finally(() => {
                    tpl.uploading = null;
                    event.target.value = ''; // Reset input
                });
        },
        removeImage(tpl, slotIndex) {
            if (!confirm('Are you sure you want to remove this custom image?')) return;

            tpl.uploading = slotIndex;

            const routeName = this.useDashboardRoutes
                ? 'dashboard.branded-assets.delete-image.for-customer'
                : 'branded-assets.delete-image';
            const params = this.useDashboardRoutes
                ? { customer: this.customer.slug, template: tpl.key }
                : { template: tpl.key };
            const url = route(routeName, params);

            axios
                .delete(url, { data: { slot: slotIndex } })
                .then(() => {
                    tpl.cacheKey = Date.now();
                    tpl.imageLoading = true; // Start loading spinner for image fetch

                    // Safety timeout
                    setTimeout(() => {
                        if (tpl.imageLoading) tpl.imageLoading = false;
                    }, 5000);

                    if (tpl.slots && tpl.slots.length > 0) {
                        const slot = tpl.slots.find((s) => s.index === slotIndex);
                        if (slot) slot.has_custom_image = false;
                    } else {
                        tpl.has_custom_main_image = false;
                    }
                })
                .catch((error) => {
                    console.error('Error removing image:', error);
                    alert('Failed to remove image');
                })
                .finally(() => {
                    tpl.uploading = null;
                });
        },
        downloadPNG(tpl) {
            tpl.downloading = true;

            const currentBrandColour = this.localBrandColour || this.brandColour;
            const brand =
                currentBrandColour && currentBrandColour.startsWith('#')
                    ? currentBrandColour
                    : this.brandColors.find((c) => c.value === currentBrandColour)?.hex || '#073141';
            const background = this.localBackgroundColour || this.backgroundColour || '#f5f5f5';
            const textColour = this.localTextColour || '#000';
            const website = this.localWebsiteUrl || this.websiteUrl || '';
            const logoUrl = this.getLogoUrl || '';

            const qsObj = {
                brand_colour: brand,
                website_url: website,
                format: 'svg',
                text_colour: textColour,
                background_colour: background,
            };

            if (this.localTextColour) {
                qsObj.text_colour = this.localTextColour;
            }

            if (this.localBackgroundColour) {
                qsObj.background_colour = this.localBackgroundColour;
            }

            if (logoUrl && !logoUrl.startsWith('blob:')) {
                qsObj.logo_url = logoUrl;
            }

            const qs = new URLSearchParams(qsObj).toString();

            const routeName = this.useDashboardRoutes
                ? 'dashboard.branded-assets.generate.for-customer'
                : 'branded-assets.generate';
            const params = this.useDashboardRoutes
                ? { customer: this.customer.slug, template: tpl.key }
                : { template: tpl.key };
            const url = route(routeName, params) + '?' + qs;

            fetch(url)
                .then((res) => {
                    if (!res.ok) throw new Error('Network response was not ok');
                    return res.text();
                })
                .then((svgText) => {
                    const img = new Image();
                    const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
                    const blobUrl = URL.createObjectURL(blob);

                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        let w = img.width || img.naturalWidth;
                        let h = img.height || img.naturalHeight;
                        if (!w || !h) {
                            w = 1200;
                            h = 1200;
                        }
                        canvas.width = w;
                        canvas.height = h;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0, w, h);
                        URL.revokeObjectURL(blobUrl);
                        try {
                            const pngUrl = canvas.toDataURL('image/png');
                            const link = document.createElement('a');
                            link.href = pngUrl;
                            link.download = `branded-asset-${tpl.key}.png`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        } catch (e) {
                            console.error('Canvas export failed', e);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `branded-asset-${tpl.key}.svg`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        }
                        tpl.downloading = false;
                    };
                    img.onerror = () => {
                        console.error('Image load failed');
                        tpl.downloading = false;
                        alert('Failed to process image. Try again.');
                    };
                    img.src = blobUrl;
                })
                .catch((err) => {
                    console.error(err);
                    tpl.downloading = false;
                    alert('Failed to download asset.');
                });
        },
        imageLoaded(tpl) {
            tpl.imageLoading = false;
        },
        clearInput(input) {
            if (input === 'brand_colour') {
                this.localBrandColour = '#073141';
            } else if (input === 'background_colour') {
                this.localBackgroundColour = '#F5F5F5';
            } else if (input === 'text_colour') {
                this.localTextColour = '#083141';
            } else if (input === 'website_url') {
                this.localWebsiteUrl = '';
            }
        },
    },
};
</script>
